// Vue
import { ref, watch } from "vue";

// Api
import { getTransportInventory } from "@/api/services";

// Dependencies
import { defineStore } from "pinia";

// Stores
import { useProjectRevisionsStore } from "@/pages/ProjectVersionHistory/stores/projectRevisions.store";
import { useDashboard } from "@/stores/dashboard/dashboard.store";
import { modelFilterForRequest, modelFilterRequestKey } from "@/stores/dashboard/selectedModelsFilter";

// Types
import { TransportInventoryType } from "../types/TransportInventoryType";

// Utils

interface TransportInventory {
  // Getters
  getInventory: () => TransportInventoryType;

  // Setters
  setProjectId: (newProjectId: string) => void;

  // Methods
  fetchInventory: (projectId: string) => Promise<void>;

  // Utility
  getLoading: () => boolean;
  getError: () => boolean;
}

const useTransportInventory = defineStore("TransportInventory", (): TransportInventory => {
  // Stores

  const dashboardStore = useDashboard();

  // Refs

  const inventory = ref<TransportInventoryType>([]);
  const projectId = ref<string>("");

  // Getters

  function getInventory() {
    return inventory.value;
  }

  // Setters

  function setInventory(newInventory: TransportInventoryType) {
    inventory.value = newInventory;
  }

  function setProjectId(newProjectId: string) {
    projectId.value = newProjectId;
  }

  // API Methods

  async function fetchInventory(projectId: string) {
    // Start loading
    apiInventoryLoading.value = true;
    apiInventoryError.value = false;

    // Pinned to the revision being read (as-of), resolved at call time so every call site stays revision-correct.
    const revisionId = useProjectRevisionsStore().readPinRevisionIdFor(projectId) ?? undefined;

    // The page has no model filter of its own; resolved at call time so the mapping wizard call site
    // narrows correctly too. Undefined whenever the selection covers the whole project.
    const models = modelFilterForRequest(dashboardStore.models, dashboardStore.selectedModels);

    // Fetch speckle branches
    try {
      const { items } = await getTransportInventory(projectId, revisionId, models);

      // Set the inventory
      setInventory(items);

      // Stop loading
      apiInventoryLoading.value = false;
    } catch (error) {
      console.error(error);
      apiInventoryError.value = true;
      apiInventoryLoading.value = false;
    }
  }

  // Methods

  // Watchers

  watch(projectId, (newProjectId) => {
    if (newProjectId) {
      fetchInventory(newProjectId);
    }
  });

  // Two-part key (pin + active revision id) per versioning.md: the pin covers previewing, the
  // pointer covers an admin's revision switch.
  const revisionsStore = useProjectRevisionsStore();
  watch(
    () =>
      projectId.value
        ? `${revisionsStore.readPinRevisionIdFor(projectId.value)}|${revisionsStore.activeMetaFor(projectId.value)?.id}`
        : "",
    () => {
      if (projectId.value) void fetchInventory(projectId.value);
    }
  );

  // Keyed on the request the Models selection produces, not the raw array, so the dashboard's
  // first-response seed-all (identical to the initial fetch) doesn't put a duplicate request on the wire.
  watch(
    () => modelFilterRequestKey(dashboardStore.models, dashboardStore.selectedModels),
    () => {
      if (projectId.value) void fetchInventory(projectId.value);
    }
  );

  // Utility

  const apiInventoryLoading = ref<boolean>(false);
  const apiInventoryError = ref<boolean>(false);
  const loading = ref<boolean>(false);
  const error = ref<boolean>(false);

  function getError() {
    return error.value;
  }

  function getLoading() {
    return loading.value;
  }

  watch([apiInventoryLoading], ([newLoading1]) => {
    loading.value = newLoading1;
  });

  watch([apiInventoryError], ([newApiInventoryError]) => {
    console.error({
      newApiInventoryError,
    });
    error.value = true;
  });

  // Returns

  return {
    // Getters
    getInventory,

    // Setters
    setProjectId,

    // Methods
    fetchInventory,

    // Utility
    getLoading,
    getError,
  };
});

export default useTransportInventory;
